import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { RoleGate } from '@/components/layout/RoleGate';
import { Shell } from '@/components/layout/Shell';
import { useSession } from '@/lib/session';
import { homeFor } from '@/lib/nav.config';
import { lazyWithRetry } from '@/lib/lazyWithRetry';

// Lazy-loaded pages with chunk reload retry
const Login = lazyWithRetry(() => import('@/pages/Login'));
const NotFound = lazyWithRetry(() => import('@/pages/NotFound'));
const Kit = lazyWithRetry(() => import('@/pages/Kit'));
const GateDisplay = lazyWithRetry(() => import('@/pages/GateDisplay'));
const AdminShell = lazyWithRetry(() => import('@/admin/AdminShell').then((m) => ({ default: m.AdminShell })));

// Role Home Pages
const PrincipalOverview = lazyWithRetry(() => import('@/pages/principal/Overview'));
const PrincipalStudents = lazyWithRetry(() => import('@/pages/principal/Students'));
const PrincipalStudentDetail = lazyWithRetry(() => import('@/pages/principal/StudentDetail'));
const PrincipalTeachers = lazyWithRetry(() => import('@/pages/principal/Teachers'));
const PrincipalAttendance = lazyWithRetry(() => import('@/pages/principal/Attendance'));
const PrincipalDevices = lazyWithRetry(() => import('@/pages/principal/Devices'));
const PrincipalFees = lazyWithRetry(() => import('@/pages/principal/Fees'));
const PrincipalAcademics = lazyWithRetry(() => import('@/pages/principal/Academics'));
const PrincipalLeaves = lazyWithRetry(() => import('@/pages/principal/Leaves'));
const PrincipalNotices = lazyWithRetry(() => import('@/pages/principal/NoticesPage'));
const PrincipalChat = lazyWithRetry(() => import('@/pages/principal/ChatPage'));
const PrincipalEvents = lazyWithRetry(() => import('@/pages/principal/EventsPage'));

const TeacherToday = lazyWithRetry(() => import('@/pages/teacher/Today'));
const TeacherAttendance = lazyWithRetry(() => import('@/pages/teacher/Attendance'));
const TeacherStudents = lazyWithRetry(() => import('@/pages/teacher/Students'));
const TeacherHomework = lazyWithRetry(() => import('@/pages/teacher/Homework'));
const TeacherMarks = lazyWithRetry(() => import('@/pages/teacher/Marks'));
const TeacherLeaves = lazyWithRetry(() => import('@/pages/teacher/Leaves'));
const TeacherNotices = lazyWithRetry(() => import('@/pages/teacher/NoticesPage'));
const TeacherChat = lazyWithRetry(() => import('@/pages/teacher/ChatPage'));
const TeacherNotifications = lazyWithRetry(() => import('@/pages/teacher/NotificationsPage'));
const TeacherProfile = lazyWithRetry(() => import('@/pages/teacher/ProfilePage'));

const StudentHome = lazyWithRetry(() => import('@/pages/student/Home'));
const StudentAttendance = lazyWithRetry(() => import('@/pages/student/Attendance'));
const StudentHomework = lazyWithRetry(() => import('@/pages/student/Homework'));
const StudentResults = lazyWithRetry(() => import('@/pages/student/Results'));
const StudentFees = lazyWithRetry(() => import('@/pages/student/Fees'));
const StudentTimetable = lazyWithRetry(() => import('@/pages/student/Timetable'));
const StudentNotices = lazyWithRetry(() => import('@/pages/student/NoticesPage'));
const StudentChat = lazyWithRetry(() => import('@/pages/student/ChatPage'));
const StudentNotifications = lazyWithRetry(() => import('@/pages/student/NotificationsPage'));
const StudentProfile = lazyWithRetry(() => import('@/pages/student/ProfilePage'));

const ParentHome = lazyWithRetry(() => import('@/pages/parent/Home'));
const ParentAttendance = lazyWithRetry(() => import('@/pages/parent/Attendance'));
const ParentFees = lazyWithRetry(() => import('@/pages/parent/Fees'));
const ParentResults = lazyWithRetry(() => import('@/pages/parent/Results'));
const ParentHomework = lazyWithRetry(() => import('@/pages/parent/Homework'));
const ParentTimetable = lazyWithRetry(() => import('@/pages/parent/Timetable'));
const ParentNotices = lazyWithRetry(() => import('@/pages/parent/NoticesPage'));
const ParentChat = lazyWithRetry(() => import('@/pages/parent/ChatPage'));
const ParentNotifications = lazyWithRetry(() => import('@/pages/parent/NotificationsPage'));
const ParentProfile = lazyWithRetry(() => import('@/pages/parent/ProfilePage'));

// Shared Notifications and Profile for Principal
const SharedNotifications = lazyWithRetry(() => import('@/components/features/Notifications'));
const SharedProfile = lazyWithRetry(() => import('@/components/features/Profile'));

// Common placeholder component for secondary sub-pages
const PlaceholderPage = lazyWithRetry(() => import('@/pages/common/PlaceholderPage'));

function RootRedirect() {
  const { user, isLoggedIn } = useSession();
  if (!isLoggedIn || !user) {
    return <Navigate to="/login" replace />;
  }
  return <Navigate to={homeFor(user.role)} replace />;
}

export function AppRoutes() {
  return (
    <Routes>
      {/* Root redirect */}
      <Route path="/" element={<RootRedirect />} />

      {/* Public Login Route */}
      <Route path="/login" element={<Login />} />

      {/* Public Gate Exhibition Projector Display */}
      <Route path="/display" element={<GateDisplay />} />

      {/* Operator Admin Console Route */}
      <Route path="/admin" element={<AdminShell />} />
      <Route path="/admin/:tab" element={<AdminShell />} />

      {/* Dev UI Component Kit Showroom */}
      <Route
        path="/_kit"
        element={
          <Shell />
        }
      >
        <Route index element={<Kit />} />
      </Route>

      {/* Principal Routes */}
      <Route
        path="/principal"
        element={
          <RoleGate role="principal">
            <Shell />
          </RoleGate>
        }
      >
        <Route index element={<Navigate to="/principal/overview" replace />} />
        <Route path="overview" element={<PrincipalOverview />} />
        <Route path="students" element={<PrincipalStudents />} />
        <Route path="students/:id" element={<PrincipalStudentDetail />} />
        <Route path="teachers" element={<PrincipalTeachers />} />
        <Route path="attendance" element={<PrincipalAttendance />} />
        <Route path="devices" element={<PrincipalDevices />} />
        <Route path="fees" element={<PrincipalFees />} />
        <Route path="academics" element={<PrincipalAcademics />} />
        <Route path="leaves" element={<PrincipalLeaves />} />
        <Route path="notices" element={<PrincipalNotices />} />
        <Route path="chat" element={<PrincipalChat />} />
        <Route path="events" element={<PrincipalEvents />} />
        <Route path="notifications" element={<SharedNotifications />} />
        <Route path="profile" element={<SharedProfile />} />
      </Route>

      {/* Teacher Routes */}
      <Route
        path="/teacher"
        element={
          <RoleGate role="teacher">
            <Shell />
          </RoleGate>
        }
      >
        <Route index element={<Navigate to="/teacher/today" replace />} />
        <Route path="today" element={<TeacherToday />} />
        <Route path="attendance" element={<TeacherAttendance />} />
        <Route path="students" element={<TeacherStudents />} />
        <Route path="homework" element={<TeacherHomework />} />
        <Route path="marks" element={<TeacherMarks />} />
        <Route path="leaves" element={<TeacherLeaves />} />
        <Route path="notices" element={<TeacherNotices />} />
        <Route path="chat" element={<TeacherChat />} />
        <Route path="notifications" element={<TeacherNotifications />} />
        <Route path="profile" element={<TeacherProfile />} />
      </Route>

      {/* Student Routes */}
      <Route
        path="/student"
        element={
          <RoleGate role="student">
            <Shell />
          </RoleGate>
        }
      >
        <Route index element={<Navigate to="/student/home" replace />} />
        <Route path="home" element={<StudentHome />} />
        <Route path="attendance" element={<StudentAttendance />} />
        <Route path="homework" element={<StudentHomework />} />
        <Route path="results" element={<StudentResults />} />
        <Route path="fees" element={<StudentFees />} />
        <Route path="timetable" element={<StudentTimetable />} />
        <Route path="notices" element={<StudentNotices />} />
        <Route path="chat" element={<StudentChat />} />
        <Route path="notifications" element={<StudentNotifications />} />
        <Route path="profile" element={<StudentProfile />} />
      </Route>

      {/* Parent Routes */}
      <Route
        path="/parent"
        element={
          <RoleGate role="parent">
            <Shell />
          </RoleGate>
        }
      >
        <Route index element={<Navigate to="/parent/home" replace />} />
        <Route path="home" element={<ParentHome />} />
        <Route path="attendance" element={<ParentAttendance />} />
        <Route path="fees" element={<ParentFees />} />
        <Route path="results" element={<ParentResults />} />
        <Route path="homework" element={<ParentHomework />} />
        <Route path="timetable" element={<ParentTimetable />} />
        <Route path="notices" element={<ParentNotices />} />
        <Route path="chat" element={<ParentChat />} />
        <Route path="notifications" element={<ParentNotifications />} />
        <Route path="profile" element={<ParentProfile />} />
      </Route>

      {/* Wildcard 404 Route */}
      <Route
        path="*"
        element={
          <Shell />
        }
      >
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
